'use client';

/**
 * ModelBusyRibbon — Turn 15's mobile-only presentation for MODEL_BUSY
 * notices (15f "mobile parity"). On mobile the 30px top bar
 * (call-top-bar-status.tsx) already carries the state itself (the shimmer
 * for WAITING, the dimmed orb + "Resting a moment" for EXHAUSTED), so these
 * notices don't need the full floating-card anatomy call-notice.tsx renders
 * on desktop — they render as a slim ribbon flush under the bar instead,
 * with its own smaller orb-echo/icon-tile/chip geometry (a ribbon is part
 * of the bar's own visual stack, not a floating card over the transcript).
 *
 * Extracted out of call-screen.tsx for the same reason call-top-bar-
 * status.tsx was (see that file's header) — a mobile-only presentational
 * branch that would otherwise bloat the orchestrator.
 *
 * Purely presentational, same division of labor as call-notice.tsx: the
 * notice payload/lifecycle (which state, the release-exit) lives in
 * useCallMode; this file only renders whatever `notice`/`releasing` it's
 * handed. Copy here is the ribbon's OWN shorter set from the 15f spec, not
 * `notice.title`/`.subtitle` verbatim — the desktop card's longer copy
 * doesn't fit this compressed row (EXHAUSTED's ribbon line differs from the
 * card's; PATIENCE's is the same locked copy because the design calls it
 * out as fitting as-is).
 */

import { Mic, TriangleAlert } from 'lucide-react';
import {
  CallNoticeKind,
  type CallNotice as CallNoticeData,
} from '@/types/chat';
import { cn } from '@/lib/utils';
import { PRESSABLE, V3_COLORS } from '@/lib/design-tokens';
import { PatienceSweepTrack } from './call-notice';

export interface ModelBusyRibbonProps {
  notice: CallNoticeData;
  /** Turn 15e "the exhale" — see call-notice.tsx's `releasing` doc; the
   *  same release-downward exit, just on this ribbon's own geometry. */
  releasing?: boolean;
  className?: string;
}

export function ModelBusyRibbon({
  notice,
  releasing = false,
  className,
}: ModelBusyRibbonProps) {
  const exitClass = releasing
    ? 'motion-safe:animate-call-notice-release motion-reduce:animate-call-notice-release-reduced'
    : undefined;

  if (notice.kind === CallNoticeKind.PATIENCE) {
    return (
      <div
        className={cn(
          'relative flex items-center gap-[10px] overflow-hidden border-b-[1px] border-[#E8E1D4] bg-[#F5F1EA] px-[12px] py-[9px]',
          exitClass,
          className
        )}
      >
        <span
          aria-hidden="true"
          className="size-[20px] shrink-0 rounded-full opacity-[.9]"
          style={{
            background: 'radial-gradient(circle at 50% 34%, #FFE8B6, #EC5F1C)',
          }}
        />
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[#31364E]">
          {notice.title}
        </span>
        <PatienceSweepTrack heightPx={2.5} />
      </div>
    );
  }

  // EXHAUSTED — kept as WARNING/amber, never red (see call-notice.ts's
  // exhaustedNotice() doc for why).
  return (
    <div
      className={cn(
        'flex items-center gap-[10px] border-b-[1px] px-[12px] py-[9px]',
        exitClass,
        className
      )}
      style={{
        backgroundColor: V3_COLORS.amberBg,
        borderBottomColor: V3_COLORS.amberBorder,
      }}
    >
      <span
        className="flex size-[24px] shrink-0 items-center justify-center rounded-[7px]"
        style={{ backgroundColor: V3_COLORS.amberTile }}
      >
        <TriangleAlert
          strokeWidth={2}
          className="size-[13px]"
          style={{ color: V3_COLORS.amber }}
        />
      </span>
      <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[#9A7534]">
        Having trouble responding.
      </span>
      {notice.action && (
        <button
          type="button"
          onClick={notice.action.onClick}
          className={cn(
            'inline-flex h-[26px] shrink-0 items-center gap-[4px] rounded-[8px] bg-[#E8A94E] px-[11px] text-[12.5px] font-semibold text-[#5f4718]',
            PRESSABLE
          )}
        >
          <Mic strokeWidth={2.2} className="size-[11px]" />
          {notice.action.label}
        </button>
      )}
    </div>
  );
}
